import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { ChartBarIcon, BuildingIcon, GlobeAltIcon, UserGroupIcon, CurrencyDollarIcon } from './Icons';

interface DashboardStats {
  total_tenants: number;
  active_tenants: number;
  total_websites: number;
  total_leads: number;
  total_qualified_revenue_leads: number;
  total_revenue: string;
  total_events: number;
  events_last_24h: number;
}

const formatRevenue = (v: string) => {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const Overview: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await apiClient.get('/platform/admin/dashboard/stats/');
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  const metrics = stats
    ? [
        { label: 'Total Tenants', value: stats.total_tenants, icon: <BuildingIcon />, color: 'amber' },
        { label: 'Active Tenants', value: stats.active_tenants, icon: <BuildingIcon />, color: 'emerald' },
        { label: 'Total Websites', value: stats.total_websites, icon: <GlobeAltIcon />, color: 'blue' },
        { label: 'Total Leads', value: stats.total_leads, icon: <UserGroupIcon />, color: 'violet' },
        { label: 'Qualified (revenue) leads', value: stats.total_qualified_revenue_leads, icon: <UserGroupIcon />, color: 'emerald' },
        { label: 'Total Revenue', value: formatRevenue(stats.total_revenue), icon: <CurrencyDollarIcon />, color: 'emerald' },
        { label: 'Total Events', value: stats.total_events.toLocaleString(), icon: <ChartBarIcon />, color: 'slate' },
        { label: 'Events (24h)', value: stats.events_last_24h.toLocaleString(), icon: <ChartBarIcon />, color: 'blue' },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">System Overview</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-fg-secondary text-sm"
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      {loading ? (
        <div className="text-fg-muted">Loading metrics...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="bg-app border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm text-fg-muted">{m.label}</p>
                  <p className="text-xl font-semibold text-white mt-0.5">{m.value}</p>
                </div>
                <div className="text-slate-600">{m.icon}</div>
              </div>
            ))}
          </div>
          <div className="bg-app border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Link
                to="/platform/admin/tenants"
                className="p-4 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 transition-colors text-left"
              >
                <p className="font-medium text-white">Tenants</p>
                <p className="text-sm text-fg-muted">Manage tenants</p>
              </Link>
              <Link
                to="/platform/admin/websites"
                className="p-4 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 transition-colors text-left"
              >
                <p className="font-medium text-white">Websites</p>
                <p className="text-sm text-fg-muted">Tracking management</p>
              </Link>
              <Link
                to="/platform/admin/leads"
                className="p-4 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 transition-colors text-left"
              >
                <p className="font-medium text-white">Leads</p>
                <p className="text-sm text-fg-muted">Global lead inspection</p>
              </Link>
              <Link
                to="/platform/admin/revenue"
                className="p-4 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 transition-colors text-left"
              >
                <p className="font-medium text-white">Revenue</p>
                <p className="text-sm text-fg-muted">Billing overview</p>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Overview;
